/**
 * Honest offline-vs-real-failure classification for `httpsCallable` errors.
 *
 * THE DEFECT THIS CLOSES (lane-tech-offline-actions, refs #1871, #4919). Every
 * FSR create/update/submit/delete/review action, and the ticket-screen status
 * actions (briefing reviewed, prediction confirmed, feedback submitted), go
 * through `httpsCallable`, which has no offline transport. Before this file,
 * a technician offline and a technician hitting a real server validation
 * error saw the identical generic toast ("Failed to submit form"). Offline is
 * expected and safe to retry the moment signal returns; a real server error
 * is not, and burying it under a misleading "offline" message would be just
 * as dishonest in the other direction — so this only reclassifies the
 * specific error shapes a network failure can actually produce, and leaves
 * every other code's message untouched.
 *
 * SIGNAL SOURCE — verified against the SDK actually shipped in this repo, not
 * guessed (`node_modules/@firebase/functions/dist/index.cjs.js`,
 * `postJSON` + `codeForHTTPStatus` + `_errorForResponse` + `callAtURL`):
 *
 *   - `fetch` itself never resolves a response (offline, DNS failure, no
 *     radio) -> `postJSON`'s catch swallows the thrown exception and returns
 *     `{ status: 0, json: null }` -> `codeForHTTPStatus(0)` maps HTTP status 0
 *     to code `'internal'`, and `_errorForResponse` stamps the httpStatus into
 *     the message: `"internal [0]"`. A REAL backend 500 also maps to
 *     `'internal'`, but carries `"...[500]"` (or the server's own message) —
 *     the trailing status in the message is what tells the two apart. This is
 *     the SDK's own disambiguation; there is no other one available client-side
 *     (the SDK's comment on this exact line states it cannot tell an unhandled
 *     backend crash from a network failure either).
 *   - No response within the callable's own client-side deadline (default
 *     70s, or an explicit `timeout` option) -> code `'deadline-exceeded'`,
 *     which the CLIENT raises via its own timer, not a status the server
 *     sent — this fires on a stalled/degraded link exactly as it would fully
 *     offline.
 *
 * `unavailable` (HTTP 503, server load-shedding / cold-start backpressure) is
 * DELIBERATELY NOT in the offline-like set above (round 2 fix). A 503 can
 * happen while the device is genuinely online — the server, not the link, is
 * the bottleneck — so calling it "offline" would misdirect a technician who
 * has signal into waiting for signal that was never the problem. It gets its
 * own classification, {@link isServerUnavailableError}, and its own message.
 *
 * Every other code (`permission-denied`, `unauthenticated`, `invalid-argument`,
 * `not-found`, `failed-precondition`, `already-exists`, …) is a real,
 * addressable server decision that reached the backend and came back with an
 * answer — never reclassified here, and — see {@link describeCallableFailure}
 * — never overridden by a caller's `isKnownOffline` claim either.
 *
 * WHY NOT `client-tech/src/lib/connectionHealth.ts`. That hysteresis signal
 * (#4021) is the right one for the tech PWA's own UI (the "you're offline"
 * banner, the photo sync loop) but this file lives in `client/src`, shared
 * build-for-build with the office app via a ONE-DIRECTIONAL alias
 * (`client-tech/tsconfig.json` maps `@shared/*` to `client/src/*`; `client`
 * has no alias back). Importing it here would break the office app's build.
 * The error-code classification above is independent of that limitation: a
 * genuine network failure produces one of the two shapes above regardless
 * of which app's `navigator.onLine` is reporting what, so it does not need
 * connectionHealth's hysteresis to be correct. `isDeviceOffline()` below adds
 * only the cheap, universally-available `navigator.onLine` signal as an
 * additional (not exclusive) trigger for the honest message — it can under-
 * report (the #4021 finding) but it can never OVER-report, so using it only
 * ever widens, never narrows, when the honest message is shown — EXCEPT for
 * the codes in {@link DEFINITELY_SERVER_REACHED_CODES}, which it can never
 * widen into either. See the security note on describeCallableFailure.
 */

const NETWORK_STATUS_ZERO_RE = /\[0\]\s*$/;

/** The subset of a FirebaseError this module reads. Avoids an SDK import for a type-only need. */
interface CallableErrorShape {
  code?: unknown;
  message?: unknown;
}

/**
 * Codes that can ONLY be produced by a request that reached the server and got an
 * explicit decision back — never a network-layer failure. Round-2 security/code fix:
 * these must never be reclassified as offline, even when a caller passes
 * `isKnownOffline: true` (a stale or racy `navigator.onLine` read, or a caller's own
 * over-eager signal). Converting a real permission/validation rejection into "you're
 * offline, saved on this phone" would hide an auth failure behind a reassuring lie.
 */
const DEFINITELY_SERVER_REACHED_CODES = new Set([
  'functions/permission-denied',
  'functions/unauthenticated',
  'functions/invalid-argument',
  'functions/failed-precondition',
  'functions/not-found',
  'functions/already-exists',
]);

/**
 * True when `error` carries a code that only a request that REACHED the server can produce
 * ({@link DEFINITELY_SERVER_REACHED_CODES}). Accepts the code with or without the
 * `functions/` prefix (callables carry it, Firestore and some wrappers do not). A caller that
 * shows "needs signal" wording must check this FIRST, so a stale `navigator.onLine` read never
 * hides a real permission/validation refusal behind a reassuring offline message.
 */
export function isServerReachedError(error: unknown): boolean {
  const code = callableErrorCode(error);
  if (!code) return false;
  return DEFINITELY_SERVER_REACHED_CODES.has(code.includes('/') ? code : `functions/${code}`);
}

function callableErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const { code } = error as CallableErrorShape;
  return typeof code === 'string' ? code : null;
}

/**
 * True when `error` is the shape a genuine network failure produces from an
 * `httpsCallable` invocation (see module doc for the two cases and their SDK
 * provenance). False for any other FunctionsErrorCode — including
 * `functions/unavailable`, which is classified separately by
 * {@link isServerUnavailableError} — and false for a value that is not an
 * Error-shaped object at all (never crash the caller's error handler over a
 * malformed input).
 */
export function isOfflineLikeFunctionsError(error: unknown): boolean {
  const code = callableErrorCode(error);
  if (!code) return false;

  if (code === 'functions/deadline-exceeded') {
    return true;
  }
  if (code === 'functions/internal') {
    // Distinguish "fetch never got a response" (httpStatus 0, offline) from a
    // real backend 500 — both map to 'internal', only the message differs.
    const { message } = error as CallableErrorShape;
    return typeof message === 'string' && NETWORK_STATUS_ZERO_RE.test(message);
  }
  return false;
}

/**
 * True when a Submit got NO ANSWER, so the tech app may queue it (the queue resends under the same client-minted report id and the
 * server upserts by it, so a request that landed with its reply lost cannot be filed twice). Never a server answer.
 *
 * Wider than {@link isOfflineLikeFunctionsError} on purpose, and used ONLY by the Submit path: the tech app bundles functions SDK
 * 0.13 (firebase 12), which words a request that got no response as code `internal` + message EXACTLY `internal` + no details
 * (measured in the tech e2e, 2026-11-01), without the " [0]" suffix of SDK 0.14. Everything else on `internal` is the server
 * speaking: our own HttpsError text, an uncaught throw ("INTERNAL"), a status-suffixed 500, anything with details.
 *
 * Named limit: SDK 0.13 words an HTTP 500 whose body is not a JSON error (a Cloud Run crash / out-of-memory) and a server
 * HttpsError('internal','internal') the same way. For a Submit that means the report is queued and retried by id (the outbox
 * treats functions/internal as transient, with backoff), never filed twice and never lost; a deterministic crash would
 * keep retrying visibly in the outbox instead of showing an error. Not used by describeCallableFailure, so no other caller's wording moves.
 */
export function isSubmitNoAnswerError(error: unknown): boolean {
  if (isServerReachedError(error) || isLockedSubmissionError(error)) return false;
  if (isOfflineLikeFunctionsError(error)) return true;
  if (callableErrorCode(error) !== 'functions/internal') return false;
  const { message, details } = error as CallableErrorShape & { details?: unknown };
  return message === 'internal' && details === undefined;
}

/**
 * True for `functions/unavailable` (HTTP 503 — server load-shedding or cold-start
 * backpressure). Deliberately NOT part of {@link isOfflineLikeFunctionsError}: unlike
 * a client-side timeout or a fetch that never got a response, a 503 is the SERVER
 * saying it is overloaded, which happens exactly as easily while the requesting
 * device is fully online. Treating it as "offline" would misdirect a technician who
 * has signal into waiting for signal that was never the problem (round 2 fix).
 */
export function isServerUnavailableError(error: unknown): boolean {
  return callableErrorCode(error) === 'functions/unavailable';
}

/**
 * True for `functions/internal` of any HTTP status (a server 500). Not part of
 * {@link isOfflineLikeFunctionsError}, which treats only "[0]" as a lost request: a 500 is an answer, and
 * the caller decides whether it may be repeated (lib/forms/retryLostSave reads the report back first).
 */
export function isInternalServerError(error: unknown): boolean {
  return callableErrorCode(error) === 'functions/internal';
}

/**
 * Cheap, universally-available connectivity check. Can under-report (a
 * degraded link where the interface is still "up") but never over-reports —
 * see module doc. Safe to call outside a browser (SSR, tests without jsdom).
 */
export function isDeviceOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * Toast id for the "no answer" notice of a Submit (useCreateFormSubmission.onError). The tech app's FormFiller queues that
 * Submit and replaces this toast with its own "Queued" (or its honest refusal), so the tech never sees both.
 */
export const NO_ANSWER_SUBMIT_TOAST_ID = 'submit-no-answer';

/** The one honest message this module shows for every offline-classified failure. */
export const OFFLINE_ACTION_MESSAGE =
  "You're offline. Your work is saved on this phone — it'll be right here when you have signal.";

/**
 * Shown for a `functions/unavailable` failure while the device reports online — the
 * server, not the connection, is the bottleneck, so this deliberately does NOT claim
 * the technician is offline (round 2 fix).
 */
export const SERVER_UNAVAILABLE_MESSAGE =
  "We can't reach the server right now. Your work is saved on this phone; try again in a moment.";

/**
 * Wording for screens that keep NOTHING on the device (the office app: a reply, an approval, a part, a schedule).
 * "Saved on this phone" is false there, and after a timeout it is doubly false: the server may have saved it.
 * Three cases, three sentences, each true:
 *   - the device is offline and the request never left -> NOT sent;
 *   - no answer came back (timeout / dropped link) -> we cannot tell, so the user must look before resending
 *     (resending blind can double an email or an approval);
 *   - a 503 from the server -> the request was refused before it ran, so NOT saved.
 */
export const NOT_SENT_OFFLINE_MESSAGE =
  "You're offline, so this may not have been sent. Reconnect, check that it went through, then try again.";
export const UNSURE_IF_SAVED_MESSAGE =
  "No answer came back, so we can't tell whether this was saved. Refresh and check before you try again.";
export const NOT_SAVED_SERVER_UNAVAILABLE_MESSAGE =
  "The server isn't answering right now, so this may not have been saved. Check before you try again.";

/**
 * Sentry OFFICEHUB-CLIENT-71. The server refuses a write to a report that is no longer editable, with
 * failed-precondition and one of TWO sentences (formSubmissionService.ts), neither with a structured field:
 *   - CREATE/upsert path (the offline form): `This submission is "<status>" and can no longer be changed from
 *     the offline form.` for every status outside draft/submitted: reviewed, approved AND rejected.
 *   - UPDATE path (the common one, once the draft has reached the server): `This submission has been <status>
 *     and can no longer be edited. Ask a manager to amend it first.` for EDIT_LOCKED_STATUSES: approved, reviewed.
 * The generic fallback used to show the technician only "Failed to submit form" and nothing said why.
 * lib/__tests__/offlineErrors.serverText.test.ts reads the server source, so a reworded server sentence fails
 * a test instead of silently turning this back off.
 */
const LOCKED_SUBMISSION_RES = [
  /This submission is "([^"]*)" and can no longer be changed from the offline form/i,
  /This submission has been "?([a-z_]+)"? and can no longer be edited/i,
];

/** The server's refusal to write to a draft that a submitted report replaced (supersededDraftRefusal in formSubmissionService.ts). */
const SUPERSEDED_RE = /This draft was replaced by a report you already submitted(?: \(([^)]+)\))?/i;

/** The server's refusal to write to a draft its author discarded (supersededRefusalMessage in formSubmissionService.ts). */
const DISCARDED_RE = /This draft was discarded and cannot be submitted/i;

const LOCKED_TAIL =
  ' Your latest changes were NOT saved; they are only on this screen. Keep this page open or write them down before you leave.';

/** Shown when the refusal names a status this client does not know. */
export const LOCKED_SUBMISSION_MESSAGE =
  `This report can no longer be changed from here.${LOCKED_TAIL} Ask your office how to proceed.`;

/** How long (ms) the refusal stays on screen: it is an instruction to read, not a flash. */
export const LOCKED_SUBMISSION_TOAST_MS = 30_000;
/** One toast id, so the same refusal raised twice shows once. */
export const LOCKED_SUBMISSION_TOAST_ID = 'locked-submission';
/** The toast options every place that shows this refusal passes, so two callers cannot stack two toasts. */
export const LOCKED_SUBMISSION_TOAST_OPTIONS = {
  id: LOCKED_SUBMISSION_TOAST_ID,
  duration: LOCKED_SUBMISSION_TOAST_MS,
} as const;

function lockedStatus(error: unknown): string | null {
  const code = callableErrorCode(error);
  if (code !== 'functions/failed-precondition' && code !== 'failed-precondition') return null;
  const { message } = (error ?? {}) as CallableErrorShape;
  if (typeof message !== 'string') return null;
  if (SUPERSEDED_RE.test(message) || DISCARDED_RE.test(message)) return 'superseded';
  for (const re of LOCKED_SUBMISSION_RES) {
    const m = re.exec(message);
    if (m) return m[1].toLowerCase();
  }
  return null;
}

/** The id of the submitted report that replaced a retired draft, when the refusal names it. */
export function supersededReplacementId(error: unknown): string | null {
  const { message } = (error ?? {}) as CallableErrorShape;
  if (typeof message !== 'string') return null;
  const m = SUPERSEDED_RE.exec(message);
  return m?.[1] ?? null;
}

/** True for the server's refusal to change a report that is no longer editable (approved, reviewed, rejected or replaced by a submitted one). */
export function isLockedSubmissionError(error: unknown): boolean {
  return lockedStatus(error) !== null;
}

/** The status the server named in the refusal (lower case), or null when this is not that refusal. */
export function lockedSubmissionStatus(error: unknown): string | null {
  return lockedStatus(error);
}

/**
 * An approved or reviewed report is edit-locked on every path (EDIT_LOCKED_STATUSES), and a superseded draft
 * (discarded here or on another device, or replaced by a filed report) is frozen on every path, so the next
 * report for the job is a new one and the phone's stored id for it is dead. A rejected report is refused on the create path
 * only (the update path never locks it), so it may still be corrected through the update path: its pointer is kept.
 */
export function shouldRetireOnLocked(status: string | null): boolean {
  return status === 'approved' || status === 'reviewed' || status === 'superseded';
}

/** What the technician reads, true to the status the server named. */
export function lockedSubmissionMessage(status: string | null, replacementId?: string | null): string {
  switch (status) {
    case 'superseded':
      if (!replacementId) {
        return `This draft was set aside (discarded, or replaced by a report you already submitted), so it can no longer be changed.${LOCKED_TAIL} To report new work on this job, start a new report.`;
      }
      return `This draft was replaced by a report you already submitted (${replacementId}), so it can no longer be changed.${LOCKED_TAIL} Open the submitted report, or start a new one.`;
    case 'approved':
      return `This report was already approved, so it can no longer be changed.${LOCKED_TAIL} To report new work on this job, start a new report.`;
    case 'rejected':
      return `This report was rejected by a manager, so it can no longer be changed from here.${LOCKED_TAIL} Ask your office how to correct it.`;
    case 'reviewed':
      return `A manager has already reviewed this report, so it can no longer be changed.${LOCKED_TAIL} To report new work on this job, start a new report.`;
    default:
      return LOCKED_SUBMISSION_MESSAGE;
  }
}

export interface CallableFailureDescription {
  /** True when this failure was classified as offline-caused, not a real server decision. */
  offline: boolean;
  /**
   * True when classified as a transient "server unreachable" condition
   * (`functions/unavailable`) while the device itself appears online. Mutually
   * exclusive with `offline`.
   */
  serverUnavailable: boolean;
  /** The message to show the user. */
  message: string;
}

/**
 * Classify an `httpsCallable` rejection and pick the message to show.
 *
 * Precedence, highest first:
 *   1. **Security guard** — if the error code is one that can only be produced by a
 *      request that reached the server ({@link DEFINITELY_SERVER_REACHED_CODES}), the
 *      real message is shown UNCONDITIONALLY. Neither `isKnownOffline` nor the error's
 *      own shape can override this — a stale `navigator.onLine` read must never mask a
 *      real permission/validation rejection as "offline, saved on this phone".
 *   2. **Offline** — either `isKnownOffline` (defaults to {@link isDeviceOffline}, or a
 *      caller-supplied better signal such as the tech PWA's own
 *      `connectionHealth.isEffectivelyOffline()`, which this file cannot import — see
 *      module doc) or {@link isOfflineLikeFunctionsError} (catches the case where the
 *      device *thought* it was online but the request itself failed at the network
 *      layer). Either signal alone is sufficient.
 *   3. **Server unavailable** — {@link isServerUnavailableError} (`functions/unavailable`)
 *      with the device reporting online: a distinct, honest "can't reach the server"
 *      message that does not claim the device itself is offline (round 2 fix).
 *   4. Otherwise, `fallbackMessage` verbatim — this function never invents a message
 *      for a real server error.
 */
export function describeCallableFailure(
  error: unknown,
  fallbackMessage: string,
  isKnownOffline: boolean = isDeviceOffline(),
  options: { workKeptOnDevice?: boolean } = {},
): CallableFailureDescription {
  const keptOnDevice = options.workKeptOnDevice !== false;
  const code = callableErrorCode(error);

  if (isLockedSubmissionError(error)) {
    return { offline: false, serverUnavailable: false, message: lockedSubmissionMessage(lockedStatus(error), supersededReplacementId(error)) };
  }

  if (code && DEFINITELY_SERVER_REACHED_CODES.has(code)) {
    return { offline: false, serverUnavailable: false, message: fallbackMessage };
  }

  if (isKnownOffline || isOfflineLikeFunctionsError(error)) {
    if (keptOnDevice) return { offline: true, serverUnavailable: false, message: OFFLINE_ACTION_MESSAGE };
    // "Not sent" is only provable when the device itself says it is offline and nothing timed out: a timeout, or a
    // dropped link on a device that thinks it is online, may have landed after the request left.
    const noAnswer = code === 'functions/deadline-exceeded' || !isKnownOffline;
    return { offline: true, serverUnavailable: false, message: noAnswer ? UNSURE_IF_SAVED_MESSAGE : NOT_SENT_OFFLINE_MESSAGE };
  }

  if (isServerUnavailableError(error)) {
    const message = keptOnDevice ? SERVER_UNAVAILABLE_MESSAGE : NOT_SAVED_SERVER_UNAVAILABLE_MESSAGE;
    return { offline: false, serverUnavailable: true, message };
  }

  // A code that is neither a proven refusal nor a network shape (a real 500, unknown, aborted, resource-exhausted)
  // can arrive AFTER the work landed. Where nothing is kept on the device, the caller's "was not sent" fallback would be
  // a guess, and a wrong guess makes someone resend an email or text. A failure with no code at all was raised on this
  // screen and never reached the server, so its own fallback stands.
  if (!keptOnDevice && code) {
    return { offline: false, serverUnavailable: false, message: UNSURE_IF_SAVED_MESSAGE };
  }

  return { offline: false, serverUnavailable: false, message: fallbackMessage };
}
